
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import secureLocalStorage from "./storage";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { IoSend, IoAttach } from "react-icons/io5";
import { BsChatDotsFill } from "react-icons/bs";

const API = process.env.REACT_APP_API;

const TOPICS = [
  { key: "cashback", label: "Cashback related", login: true },
  { key: "payment", label: "Payment / Withdrawal", login: true },
  { key: "account", label: "Account / Profile", login: true },
  { key: "store_offer", label: "About a store or offer", login: false },
  { key: "how_it_works", label: "How CashKaroNa works", login: false },
  { key: "other", label: "Something else", login: false },
];

const getGuestId = () => {
  let id = localStorage.getItem("chat_guest_id");
  if (!id) {
    id = (window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : Date.now() + Math.random().toString(36).slice(2)
    ).replace(/[^a-zA-Z0-9_-]/g, "");
    localStorage.setItem("chat_guest_id", id);
  }
  return id;
};

const dayLabel = (d) => {
  const dt = new Date(d);
  const today = new Date();
  const yest = new Date();
  yest.setDate(today.getDate() - 1);
  if (dt.toDateString() === today.toDateString()) return "Today";
  if (dt.toDateString() === yest.toDateString()) return "Yesterday";
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const ChatWithUs = () => {
  const navigate = useNavigate();
  const token = secureLocalStorage.getItem("n_token");
  const cfg = token
    ? { headers: { token } }
    : { headers: { "x-guest-id": getGuestId() } };

  const [isOpen, setIsOpen] = useState(true);
  const [chat, setChat] = useState(null);
  const [chatLog, setChatLog] = useState([]);
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  const lastId = chatLog.length ? chatLog[chatLog.length - 1]._id : null;
  const closed = chat?.status === "closed";
  const showTopics = !token && !chat && !loading;

  // chat shuru. topic sirf guest ke liye zaroori
  const startChat = async (topic) => {
    try {
      const { data } = await axios.post(`${API}website/chat_start`, { topic }, cfg);
      setChat(data.chat);
      setChatLog(data.messages || []);
    } catch (e) {
      if (e?.response?.data?.code === "LOGIN_REQUIRED") {
        navigate("/login"); // apna login route
      } else {
        console.log("chat_start error", e);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      if (token) {
        // login ke baad guest ki chat account se jod do
        const gid = localStorage.getItem("chat_guest_id");
        if (gid) {
          try {
            await axios.post(`${API}website/chat_merge`, { guest_id: gid }, cfg);
          } catch (e) { }
          localStorage.removeItem("chat_guest_id");
        }
        await startChat("");
      } else {
        // guest: pehle se open chat ho to wahi, nahi to topic buttons
        await startChat("");
      }
    })();
    // eslint-disable-next-line
  }, []);

  const pickTopic = (t) => {
    if (t.login && !token) {
      navigate("/login"); // login ke baad wapas /help/chat-with-us khol dena
      return;
    }
    setLoading(true);
    startChat(t.key);
  };

  // polling: 10 sec, band chat par nahi
  useEffect(() => {
    if (!isOpen || !chat || closed) return;
    const id = setInterval(async () => {
      if (document.hidden) return;
      try {
        const { data } = await axios.get(`${API}website/chat_messages`, {
          ...cfg,
          params: lastId ? { after: lastId } : {},
        });
        if (data.messages?.length) {
          setChatLog((prev) => {
            const have = new Set(prev.map((m) => m._id));
            const fresh = data.messages.filter((m) => !have.has(m._id));
            return fresh.length ? [...prev, ...fresh] : prev;
          });
        }
        if (data.chat) setChat(data.chat);
      } catch (e) { }
    }, 10000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [isOpen, chat?._id, closed, lastId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatLog]);

  const handleSend = async () => {
    if ((!message.trim() && !file) || sending || closed) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("text", message);
      if (file) fd.append("attachment", file);
      const res = await axios.post(`${API}website/chat_send`, fd, cfg);
      if (res.data.message) setChatLog((prev) => [...prev, res.data.message]);
      setMessage("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      if (e?.response?.data?.code === "LOGIN_REQUIRED") {
        alert(e.response.data.message);
        navigate("/login");
      } else {
        alert(e?.response?.data?.message || "Message could not be sent, please try again");
      }
    }
    setSending(false);
  };

  // band chat ke baad nayi chat: guest ko dobara topic dikhao
  const newChat = () => {
    if (token) {
      startChat("");
    } else {
      setChat(null);
      setChatLog([]);
    }
  };

  return (
    <>
      <header>
        <Navbar />
      </header>

      <nav className="chat-breadcrumb">
        <div className="container">
          <span onClick={() => navigate("/")}>Home</span> /{" "}
          <span onClick={() => navigate("/help")}>Help</span> /{" "}
          <b>Chat with us</b>
        </div>
      </nav>

      <section className="chat-page-section">
        <div className="container">
          {isOpen && (
            <div className="chat-widget">
              <div className="chat-header">
                <div className="chat-header-top">
                  <img
                    className="chat-header-logo"
                    src="/assets/images/logo/cashkaro.png"
                    alt="cashkaroNa"
                  />
                  <button className="chat-close-btn" onClick={() => setIsOpen(false)}>
                    ✕
                  </button>
                </div>
                <p className="chat-subtitle">Currently replying in under 2 minutes</p>
              </div>

              {showTopics ? (
                <div className="chat-body">
                  <div className="chat-bubble support">
                   Hi! What can we help you with?
                  </div>
                  <div className="chat-topics">
                    {TOPICS.map((t) => (
                      <button key={t.key} onClick={() => pickTopic(t)}>
                        {t.label}
                        {t.login && <small> 🔒 login required</small>}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div className="chat-body">
                    {chatLog.map((msg, i) => {
                      const showDay =
                        i === 0 ||
                        new Date(chatLog[i - 1].createdAt).toDateString() !==
                        new Date(msg.createdAt).toDateString();
                      return (
                        <React.Fragment key={msg._id}>
                          {showDay && <div className="chat-day">{dayLabel(msg.createdAt)}</div>}
                          <div
                            className={`chat-bubble ${msg.sender_type === "user"
                                ? "user"
                                : msg.sender_type === "system"
                                  ? "system"
                                  : "support"
                              }`}
                          >
                            {msg.text}
                            {msg.attachment && (
                              <div>
                                <a
                                  href={`${API}uploads/${msg.attachment}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="chat-attach-link"
                                >
                                  📎 Attachment
                                </a>
                              </div>
                            )}
                          </div>
                        </React.Fragment>
                      );
                    })}
                    <div ref={bottomRef} />
                  </div>

                  {closed ? (
                    <div className="chat-closed-box">
                      <p>This chat has been closed.</p>
                      <button onClick={newChat}>Start a new chat</button>
                    </div>
                  ) : (
                    <>
                      {file && (
                        <div className="chat-file-chip">
                          📎 {file.name}
                          <span
                            onClick={() => {
                              setFile(null);
                              if (fileRef.current) fileRef.current.value = "";
                            }}
                          >
                            ✕
                          </span>
                        </div>
                      )}
                      <div className="chat-input-row">
                        {token && (
                          <>
                            <input
                              type="file"
                              ref={fileRef}
                              hidden
                              onChange={(e) => setFile(e.target.files[0] || null)}
                            />
                            <button
                              onClick={() => fileRef.current?.click()}
                              aria-label="Attach file"
                            >
                              <IoAttach size={20} />
                            </button>
                          </>
                        )}
                        <input
                          type="text"
                          placeholder="Reply here..."
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleSend()}
                        />
                        <button onClick={handleSend} disabled={sending} aria-label="Send message">
                          <IoSend size={18} />
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {!isOpen && (
            <button className="chat-reopen-btn" onClick={() => setIsOpen(true)}>
              <BsChatDotsFill size={18} />
              Chat with us
            </button>
          )}
        </div>
      </section>

      <Footer />
      <style jsx>{`
        .chat-breadcrumb {
          padding: 12px 0;
          font-size: 0.9rem;
          color: #555;
        }
        .chat-breadcrumb span {
          cursor: pointer;
          color: var(--primary);
        }
        .chat-page-section {
          padding: 1rem 0 4rem;
          position: relative;
        }
          .chat-bubble.user {
  background: #ff7a00;
  color: #fff;
  align-self: flex-end;
}
  .chat-day {
  align-self: center;
  font-size: 0.72rem;
  color: #777;
  background: #f1f1f1;
  padding: 2px 10px;
  border-radius: 10px;
}
.chat-bubble.system {
  align-self: center;
  background: transparent;
  color: #777;
  font-size: 0.78rem;
  text-align: center;
  max-width: 90%;
}
        .chat-widget {
          position: fixed;
          right: 20px;
          bottom: 20px;
          width: 320px;
          height: 420px;
          max-height: 80vh;
          border: 1px solid #eee;
          border-radius: 10px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
          overflow: hidden;
          background: #fff;
          z-index: 9999;
          display: flex;
          flex-direction: column;
        }
        .chat-header {
          background: var(--primary-gradient);
          color: #fff;
          padding: 0.75rem 1rem;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .chat-header-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
        }
        .chat-header-logo {
          height: 50px;
          width: auto;
          object-fit: contain;
        }
        .chat-close-btn {
          background: none;
          border: none;
          color: #fff;
          cursor: pointer;
          font-size: 1rem;
        }
        .chat-subtitle {
          font-size: 0.75rem;
          color: #fff;
          opacity: 0.85;
          margin: 0;
        }
        .chat-body {
          padding: 12px;
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .chat-bubble {
          padding: 8px 12px;
          border-radius: 12px;
          max-width: 80%;
          font-size: 0.9rem;
          word-break: break-word;
        }
        .chat-bubble.support {
          background: #f1f1f1;
          align-self: flex-start;
        }
        .chat-bubble.user {
          background: var(--primary);
          color: #fff;
          align-self: flex-end;
        }
        .chat-attach-link {
          font-size: 0.8rem;
          text-decoration: underline;
          color: inherit;
        }
        .chat-file-chip {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.78rem;
          padding: 4px 12px;
          background: #f7f7f7;
          border-top: 1px solid #eee;
          flex-shrink: 0;
        }
        .chat-file-chip span {
          cursor: pointer;
          margin-left: 8px;
        }
        .chat-closed-box {
          flex-shrink: 0;
          border-top: 1px solid #eee;
          padding: 12px;
          text-align: center;
          font-size: 0.85rem;
        }
        .chat-closed-box p {
          margin: 0 0 8px;
        }
        .chat-closed-box button {
          border: none;
          background: var(--primary-gradient);
          color: #fff;
          padding: 8px 16px;
          border-radius: 20px;
          cursor: pointer;
          font-weight: 600;
        }
        .chat-input-row {
          display: flex;
          border-top: 1px solid #eee;
          flex-shrink: 0;
        }
        .chat-input-row input[type="text"] {
          flex: 1;
          border: none;
          padding: 10px 12px;
          font-size: 0.9rem;
        }
        .chat-input-row input:focus {
          outline: none;
        }
        .chat-input-row button {
          border: none;
          background: #fff;
          padding: 0 14px;
          cursor: pointer;
          color: var(--primary);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .chat-input-row button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .chat-reopen-btn {
          position: fixed;
          right: 20px;
          bottom: 20px;
          background: var(--primary-gradient);
          color: #fff;
          border: none;
          padding: 12px 20px;
          border-radius: 30px;
          cursor: pointer;
          font-weight: 600;
          z-index: 9999;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          display: flex;
          align-items: center;
          gap: 8px;
        }
          .chat-topics { display: flex; flex-direction: column; gap: 8px; margin-top: 4px; }
.chat-topics button {
  text-align: left; border: 1px solid #ff7a00; background: #fff; color: #ff7a00;
  padding: 8px 12px; border-radius: 18px; cursor: pointer; font-size: 0.85rem;
}
.chat-topics button:hover { background: #fff3e8; }
.chat-topics small { color: #777; margin-left: 4px; }
      `}</style>
      {/* <style jsx> ka tumhara poora CSS waisa hi rehne do, bas neeche wali classes uske andar jod do */}
    </>
  );
};

export default ChatWithUs;